// IQPS_Audio.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { I_IQPlayerGallery, I_IQPlayerGallerySettings } from "./../interfaces";
import PlayAudioIcon from "../../../../ui/svg/audio/play-audio";
import StopAudioIcon from "../../../../ui/svg/audio/stop-audio";
import {Toggle} from "../../../../ui/toggle/toggle";

// лучше вынести в общий constants.ts и импортировать из обоих файлов
export const STOP_PLAY_OTHER_EVENT = 'STOP_PLAY_OTHER_EVENT';

interface I_IQPS_AudioProps {
    item: I_IQPlayerGallery;
    iQPlayerGallerySettings: I_IQPlayerGallerySettings;
    runID: string | number;
}

const IQPS_Audio: React.FC<I_IQPS_AudioProps> = ({ item, runID }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isReGen, setisReGen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const audioCtxRef = useRef<AudioContext | null>(null);
    const gainNodesRef = useRef<GainNode[]>([]);
    const sourcesRef = useRef<AudioBufferSourceNode[]>([]);
    const buffersRef = useRef<AudioBuffer[]>([]);

    const bypassIndex = useMemo(
        () => Math.max(0, item?.audioSrc?.length - 1),
        [item?.audioSrc]
    );

    const ensureContext = () => {
        if (!audioCtxRef.current) {
            audioCtxRef.current = new (window.AudioContext ||
                (window as any).webkitAudioContext)();
        }
        return audioCtxRef.current!;
    };

    const teardownSources = () => {
        sourcesRef.current.forEach((src) => {
            try { src.stop(0); } catch {}
            try { src.disconnect(); } catch {}
        });
        sourcesRef.current = [];
    };

    const createGainsIfNeeded = (count: number) => {
        const ctx = ensureContext();
        if (gainNodesRef.current.length === count) return;
        gainNodesRef.current.forEach((g) => { try { g.disconnect(); } catch {} });
        gainNodesRef.current = new Array(count).fill(null).map(() => {
            const g = ctx.createGain();
            g.gain.value = 0;
            g.connect(ctx.destination);
            return g;
        });
    };

    const loadAllBuffers = async () => {
        const ctx = ensureContext();
        const urls = item?.audioSrc || [];
        if (!urls.length) return;
        createGainsIfNeeded(urls.length);
        const arrayBuffers = await Promise.all(
            urls.map(async (url) => {
                const res = await fetch(url);
                return await res.arrayBuffer();
            })
        );
        const decoded = await Promise.all(arrayBuffers.map((ab) => ctx.decodeAudioData(ab.slice(0))));
        buffersRef.current = decoded;
    };

    const startPlayback = async () => {
        const ctx = ensureContext();
        if (ctx.state === "suspended") await ctx.resume();

        // оповестим остальных заранее
        document.dispatchEvent(
            new CustomEvent(STOP_PLAY_OTHER_EVENT, { detail: { id: runID } })
        );

        teardownSources();
        createGainsIfNeeded(item.audioSrc.length);

        sourcesRef.current = buffersRef.current.map((buf, i) => {
            const src = ctx.createBufferSource();
            src.buffer = buf;
            src.loop = true;
            src.connect(gainNodesRef.current[i]);
            return src;
        });

        const activeIndex = isReGen ? selectedIndex : bypassIndex;
        gainNodesRef.current.forEach((g, i) => (g.gain.value = i === activeIndex ? 1 : 0));
        sourcesRef.current.forEach((s) => s.start());

        setIsPlaying(true);
    };

    const stopPlayback = () => {
        teardownSources();
        setIsPlaying(false);
    };

    const switchActiveTrack = (activeIndex: number) => {
        gainNodesRef.current.forEach((g, i) => (g.gain.value = i === activeIndex ? 1 : 0));
    };

    // auto-load Buffers
    useEffect(() => {
        //console.log('item: ',item);
        loadAllBuffers();
        return () => {
            stopPlayback();
            try { audioCtxRef.current?.close(); } catch {}
            audioCtxRef.current = null;
            gainNodesRef.current = [];
            buffersRef.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item]);

    // переключение StudioVerse
    useEffect(() => {
        if (!isPlaying) return;
        const idx = isReGen ? selectedIndex : bypassIndex;
        switchActiveTrack(idx);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReGen]);

    // смена выбранного трека
    useEffect(() => {
        if (!isPlaying || !isReGen) return;
        switchActiveTrack(selectedIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndex]);

    // СЛУШАЕМ «остановить других»
    useEffect(() => {
        const onStopOther = (e: Event) => {
            const ce = e as CustomEvent<{ id: string | number }>;
            // если событие пришло не от нас — остановиться
            if (ce?.detail?.id !== runID) {
                if (isPlaying) stopPlayback();
            }
        };
        document.addEventListener(STOP_PLAY_OTHER_EVENT, onStopOther as EventListener);
        return () => {
            document.removeEventListener(STOP_PLAY_OTHER_EVENT, onStopOther as EventListener);
        };
    }, [runID, isPlaying]); // isPlaying в зависимостях ок: слушатель лёгкий

    const getCurrentTrackName = () => {
        if (!isPlaying) return "";
        const idx = isReGen ? selectedIndex : bypassIndex;
        return item?.audioSrc?.[idx] ? item.audioSrc[idx].split("/").pop() || "" : "";
    };

    return (
        <div className="rounded-3xl overflow-hidden">
            <div className={`card ${item?.cardClass} pt-6 pb-6 px-4`} style={{ color:item?.color, backgroundColor: item?.bgColor }}>
                <div className="flex justify-start">
                    <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="14.5" cy="14.5" r="14" stroke="black"/>
                        <path
                            d="M6.34203 13.9402C5.83933 14.5095 5.8975 15.3743 6.47212 15.8725L12.0108 20.662C12.5742 21.1478 13.4257 21.1052 13.9353 20.5647L23.6274 10.3015C24.1496 9.75016 24.1192 8.8839 23.5597 8.36806C23.0017 7.85219 22.1253 7.88231 21.6031 8.43506L12.8262 17.7294L8.297 13.8116C7.72102 13.3148 6.84589 13.3723 6.34203 13.9402Z"
                            fill="black"/>
                    </svg>
                </div>
                <div className="font-semibold text-xl md:text-2xl font-bold mt-4 mb-2 h-10 md:h-16">{item?.title}</div>

                {/*{isPlaying && (*/}
                {/*    <div className="text-sm mb-2 opacity-70">Playing {runID}: {getCurrentTrackName()}</div>*/}
                {/*)}*/}

                <div className="flex flex-row md:items-center justify-between gap-4">
                    <button
                        type="button"
                        aria-label={"" + (isPlaying ? "Stop" : "Play") + " for " + item?.title}
                        aria-pressed={isPlaying}
                        className=""
                        onClick={() => {
                            isPlaying ? stopPlayback() : startPlayback();
                        }}
                    >
                        {isPlaying ? (
                            <StopAudioIcon width="40" height="40" color={item?.colorEx1} bgColor={item?.color}/>
                        ) : (
                            <PlayAudioIcon width="40" height="40" color={item?.colorEx1} bgColor={item?.color}/>
                        )}
                    </button>
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none text1">
                        <span className={`text-sm ${isReGen ? "" : "font-bold"}`}>Original</span>
                        <Toggle
                            isChecked={isReGen}
                            handleColor={isReGen ? item?.colorEx1 : item?.bgColor}
                            bgClass="bg-black"
                            borderClass="border border-gray-800"
                            onChange={(checked: boolean) => setisReGen(checked)}
                            width={50}
                            // text={isReGen ? 'On' : 'Off'}
                            isPositionRight={false}
                            textStyles={{
                                marginLeft: '-5px',
                            }}
                        />
                        <span className={`text-sm ${isReGen ? "font-bold" : ""}`}>ReGen</span>
                    </label>
                </div>
            </div>

            {item?.img && (
                <img className="bg-gray-400 w-full object-cover" src={item.img} alt={item.title}/>
            )}
        </div>
    );
};

export default IQPS_Audio;
